import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Activity, Package, CreditCard, Settings, Search, LogOut, Dog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { initials } from "@/lib/format";
import { BRANDING } from "@/config/branding";
import { OfflineBanner } from "@/components/pwa/OfflineBanner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/clienti", label: "Clienti e cani", icon: Users },
  { to: "/prestazioni", label: "Prestazioni", icon: Activity },
  { to: "/pacchetti", label: "Pacchetti", icon: Package },
  { to: "/pagamenti", label: "Pagamenti", icon: CreditCard },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings },
];

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => { setRes(r.data); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <div className="relative flex-1 max-w-xl" ref={ref}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        data-testid="global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Cerca cane o proprietario…"
        className="w-full h-11 pl-9 pr-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      />
      {open && res && ((res.owners?.length ?? 0) > 0 || (res.dogs?.length ?? 0) > 0) && (
        <div className="absolute z-50 mt-2 w-full rounded-xl border border-border bg-card shadow-lg overflow-hidden">
          {res.dogs.map((d) => (
            <button key={`d${d.id}`} onClick={() => go(`/cani/${d.id}`)} data-testid={`search-dog-${d.id}`}
              className="flex w-full items-center gap-3 px-3 py-2.5 hover:bg-secondary text-left">
              <span className="grid place-items-center h-8 w-8 rounded-full bg-primary/10 text-primary text-xs font-bold"><Dog className="h-4 w-4" /></span>
              <span className="text-sm"><b>{d.nome}</b> <span className="text-muted-foreground">· {d.owner_name}</span></span>
            </button>
          ))}
          {res.owners.map((o) => (
            <button key={`o${o.id}`} onClick={() => go(`/clienti/${o.id}`)} data-testid={`search-owner-${o.id}`}
              className="flex w-full items-center gap-3 px-3 py-2.5 hover:bg-secondary text-left">
              <span className="grid place-items-center h-8 w-8 rounded-full bg-accent/15 text-accent text-xs font-bold">{initials(`${o.nome} ${o.cognome}`)}</span>
              <span className="text-sm">{o.nome} {o.cognome}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Layout = ({ children }) => {
  const { logout } = useAuth();
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/90 border-b border-border pt-[env(safe-area-inset-top)]">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <img src={BRANDING.logo} alt="ALM" className="h-9 w-9 rounded-xl object-contain bg-secondary p-0.5" />
            <span className="leading-none hidden sm:block">
              <span className="font-head font-extrabold text-lg tracking-tight block">{BRANDING.productName}</span>
              <span className="text-[10px] font-semibold text-muted-foreground">{BRANDING.productSignature}</span>
            </span>
          </div>
          <GlobalSearch />
          <NavLink to="/impostazioni" data-testid="nav-settings-header" title="Impostazioni"
            className={({ isActive }) => `grid place-items-center h-11 w-11 rounded-full border border-border shrink-0 ${isActive ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}>
            <Settings className="h-4 w-4" />
          </NavLink>
          <button data-testid="logout-btn" onClick={logout}
            className="grid place-items-center h-11 w-11 rounded-full border border-border bg-card hover:bg-secondary shrink-0">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <OfflineBanner />

      <div className="mx-auto max-w-6xl px-4 flex gap-6">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-56 shrink-0 py-6">
          <nav className="space-y-1 sticky top-20">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 py-6 pb-28 lg:pb-10 min-w-0">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/95 backdrop-blur-md safe-bottom">
        <div className="grid grid-cols-5">
          {NAV.slice(0, 5).map((n) => {
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mnav-${n.label}`}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold ${active ? "text-primary" : "text-muted-foreground"}`}>
                <n.icon className="h-5 w-5" />
                {n.label.split(" ")[0]}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
